import { appEventsMessaging } from "./hooks/pubSub";
import { rmsApi } from "../state/axios";
import { getEnvValueFor } from "./utils";

// Comprehensive enum for all result types used in offthread fetching
export enum OFFTHREAD_RESULT_TYPE {
  // Standard result types from the existing enum
  RESULT = "result",
  ERROR = "error",
  TIMEOUT = "timeout",
  MISSING_INPUT = "missing_input",
  PENDING = "pending",
  BLOCKED = "blocked",

  // Additional result types specific to offthread operations
  ERRORRED = "errorred", // Legacy string literal found in original code
  READY = "ready", // For when result is ready
}

const GLOBAL_TIMEOUT_SECONDS = parseInt(
  getEnvValueFor("GLOBAL_OFF_THREAD_QUERY_TIMEOUT") || "100",
  10
);
const WAIT_PERIOD_THRESHOLD_SECONDS = parseInt(
  getEnvValueFor("OFF_THREAD_QUERY_WAIT_PERIOD_THRESHOLD") || "20",
  10
);
const TOTAL_TIMEOUT_SECONDS =
  GLOBAL_TIMEOUT_SECONDS + WAIT_PERIOD_THRESHOLD_SECONDS;

interface ApiResponse {
  data?: {
    error?: any;
    lock_type?: string;
    widget_data?: any;
    result_meta?: {
      result_type: string;
      result_id?: string;
    };
  };
}

interface PendingResultData {
  result_id?: string;
  errorred?: boolean;
  // When the result is small enough, the backend includes it inline in the push message.
  // If present AND contains result_meta, the frontend can use it directly (no 2nd HTTP request).
  result?: any;
}

interface Subscription {
  unsubscribe: () => void;
  subscribe: () => void;
}

const safeUnsubscribe = (subscription: Subscription | undefined): void => {
  if (subscription) {
    try {
      subscription.unsubscribe();
    } catch (error) {
      console.warn("Error unsubscribing:", error);
    }
  }
};

const handleMarketingMessage = (responseData: any): boolean => {
  if (responseData.lock_type === "marketing_message") {
    if (responseData.widget_data) {
      return true;
    } else {
      throw responseData;
    }
  }
  throw responseData;
};

const handleApiError = (responseData: any): void => {
  if (responseData.lock_type) {
    handleMarketingMessage(responseData);
  } else {
    throw responseData;
  }
};

const handleResultType = (
  responseData: any,
  subscription: Subscription | undefined,
  shouldCleanup: boolean = true
): any => {
  const resultType = responseData.result_meta?.result_type;

  if (!resultType) {
    throw responseData;
  }

  switch (resultType) {
    case OFFTHREAD_RESULT_TYPE.RESULT:
    case OFFTHREAD_RESULT_TYPE.MISSING_INPUT:
    case OFFTHREAD_RESULT_TYPE.READY:
      if (shouldCleanup) {
        safeUnsubscribe(subscription);
      }
      return responseData;

    case OFFTHREAD_RESULT_TYPE.ERROR:
    case OFFTHREAD_RESULT_TYPE.ERRORRED:
    case OFFTHREAD_RESULT_TYPE.TIMEOUT:
      if (shouldCleanup) {
        safeUnsubscribe(subscription);
      }
      throw responseData;

    case OFFTHREAD_RESULT_TYPE.PENDING:
      return { type: "pending", resultId: responseData.result_meta.result_id };

    case OFFTHREAD_RESULT_TYPE.BLOCKED:
      if (shouldCleanup) {
        safeUnsubscribe(subscription);
      }
      throw responseData;

    default:
      console.warn(`Unknown result type: ${resultType}`);
      if (shouldCleanup) {
        safeUnsubscribe(subscription);
      }
      throw responseData;
  }
};

// Helper function to make API call with error handling
const makeApiCall = async (url: string, options: any): Promise<ApiResponse> => {
  try {
    const response = await rmsApi(url, options);
    return response;
  } catch (error: any) {
    // Don't log intentional cancellations (e.g. React StrictMode remount, lazy-load scroll-away)
    if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
      console.debug("[OFFTHREAD FETCHER] Request canceled (expected):", url);
    } else {
      console.error("[OFFTHREAD FETCHER] API call failed:", error);
    }
    throw error;
  }
};

// Helper function to handle pending result callback
const createPendingResultHandler = (
  url: string,
  options: any,
  resolve: (value: any) => void,
  reject: (reason?: any) => void,
  subscription: { current: Subscription | undefined },
  startTime: number
) => {
  return async (data: PendingResultData) => {
    if (!data.result_id) {
      console.warn("[OFFTHREAD FETCHER] No result_id in pending result data");
      safeUnsubscribe(subscription.current);
      const duration = Date.now() - startTime;
      console.debug(
        `[OFFTHREAD FETCHER] Fetch failed (invalid pending result) - Duration: ${duration}ms - URL: ${url}`
      );
      reject(new Error("Invalid pending result data"));
      return;
    }

    if (data.errorred) {
      safeUnsubscribe(subscription.current);
      const duration = Date.now() - startTime;

      // Enhanced error logging with more context
      console.error(
        `[OFFTHREAD FETCHER] Pending result failed - Duration: ${duration}ms - URL: ${url}`
      );
      console.error(`[OFFTHREAD FETCHER] Pending result error details:`, {
        resultId: data.result_id,
        url: url,
        options: options,
        duration: `${duration}ms`,
        errorData: data,
        timestamp: new Date().toISOString(),
      });

      const error = new Error(
        `Pending result errorred for ${url} (Duration: ${duration}ms, Result ID: ${data.result_id})`
      );
      console.debug(
        `[OFFTHREAD FETCHER] Fetch failed (pending result errorred) - Duration: ${duration}ms - URL: ${url}`
      );
      reject(error);
      return;
    }

    // If result was included inline in the push message (small enough to fit),
    // and it contains result_meta (i.e. it's a pre-formatted API response),
    // use it directly without making a 2nd HTTP request.
    if (data.result && data.result.result_meta) {
      safeUnsubscribe(subscription.current);
      const duration = Date.now() - startTime;
      console.debug(
        `[OFFTHREAD FETCHER] Using inline push result (no 2nd HTTP request) - Duration: ${duration}ms - URL: ${url}`
      );
      resolve(data.result);
      return;
    }

    // Fall back to 2nd HTTP request to fetch transformed result from server
    try {
      const pendingResultUrl = url.includes('?')
        ? `${url}&pending_result_id=${data.result_id}`
        : `${url}?pending_result_id=${data.result_id}`;
      const response = await makeApiCall(pendingResultUrl, options);

      if (response.data?.error) {
        handleApiError(response.data);
        const duration = Date.now() - startTime;
        console.debug(
          `[OFFTHREAD FETCHER] Fetch completed (marketing message) - Duration: ${duration}ms - URL: ${url}`
        );
        resolve(response.data); // Only reached if handleApiError doesn't throw (marketing message case)
      } else {
        const result = handleResultType(response.data, subscription.current);
        const duration = Date.now() - startTime;
        console.debug(
          `[OFFTHREAD FETCHER] Fetch completed (pending result ready) - Duration: ${duration}ms - URL: ${url}`
        );
        resolve(result);
      }
    } catch (error) {
      safeUnsubscribe(subscription.current);
      const duration = Date.now() - startTime;
      console.debug(
        `[OFFTHREAD FETCHER] Fetch failed (pending result error) - Duration: ${duration}ms - URL: ${url} - Error:`,
        error
      );
      reject(error);
    }
  };
};

export const fetchData = async (url: string, options: any): Promise<any> => {
  const startTime = Date.now();
  console.debug(
    `[OFFTHREAD FETCHER] Starting fetch - URL: ${url} - Timeout: ${TOTAL_TIMEOUT_SECONDS}s (${GLOBAL_TIMEOUT_SECONDS}s + ${WAIT_PERIOD_THRESHOLD_SECONDS}s)`
  );

  return new Promise(async (resolve, reject) => {
    // Use object wrapper to allow mutation in callbacks
    const subscriptionRef = { current: undefined as Subscription | undefined };
    let timeoutHandle: NodeJS.Timeout | undefined;
    let isCompleted = false;

    // Cleanup function to ensure subscription and timeout are always cleaned up
    const cleanup = () => {
      safeUnsubscribe(subscriptionRef.current);
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = undefined;
      }
    };

    // Enhanced resolve wrapper with timing and cleanup
    const resolveWithTiming = (result: any) => {
      if (isCompleted) return; // Prevent multiple calls
      isCompleted = true;

      const duration = Date.now() - startTime;
      console.debug(
        `[OFFTHREAD FETCHER] Fetch completed successfully - Duration: ${duration}ms - URL: ${url}`
      );
      cleanup();
      resolve(result);
    };

    // Enhanced reject wrapper with timing and cleanup
    const rejectWithTiming = (error: any) => {
      if (isCompleted) return; // Prevent multiple calls
      isCompleted = true;

      const duration = Date.now() - startTime;
      console.debug(
        `[OFFTHREAD FETCHER] Fetch failed - Duration: ${duration}ms - URL: ${url} - Error:`,
        error
      );
      cleanup();
      reject(error);
    };

    // Set up global timeout using total timeout value
    timeoutHandle = setTimeout(() => {
      if (isCompleted) return;

      const duration = Date.now() - startTime;
      console.error(
        `[OFFTHREAD FETCHER] Fetch timeout exceeded - Duration: ${duration}ms - URL: ${url} - Timeout: ${TOTAL_TIMEOUT_SECONDS}s`
      );

      const timeoutError = new Error(
        `Offthread query timeout exceeded (${TOTAL_TIMEOUT_SECONDS}s) for ${url} (Duration: ${duration}ms)`
      );
      timeoutError.name = "OffthreadTimeoutError";

      rejectWithTiming(timeoutError);
    }, TOTAL_TIMEOUT_SECONDS * 1000);

    try {
      const response = await makeApiCall(url, options);

      // Handle API errors first
      if (response.data?.error) {
        try {
          handleApiError(response.data);
          resolveWithTiming(response.data); // Only reached for marketing message with widget_data
          return;
        } catch (error) {
          rejectWithTiming(error);
          return;
        }
      }

      // Handle different result types
      try {
        const result = handleResultType(
          response.data,
          subscriptionRef.current,
          false
        );

        // Check if result indicates pending status
        if (result && typeof result === "object" && result.type === "pending") {
          if (!result.resultId) {
            rejectWithTiming(new Error("No result ID for pending result"));
            return;
          }

          const currentDuration = Date.now() - startTime;
          const remainingTimeout =
            TOTAL_TIMEOUT_SECONDS * 1000 - currentDuration;

          console.debug(
            `[OFFTHREAD FETCHER] Setting up pending result subscription - Result ID: ${
              result.resultId
            } - URL: ${url} - Remaining timeout: ${Math.round(
              remainingTimeout / 1000
            )}s`
          );

          // Check if we've already exceeded the wait period threshold
          if (currentDuration > WAIT_PERIOD_THRESHOLD_SECONDS * 1000) {
            console.warn(
              `[OFFTHREAD FETCHER] Wait period threshold exceeded (${WAIT_PERIOD_THRESHOLD_SECONDS}s) - Duration: ${currentDuration}ms - URL: ${url}`
            );
          }

          // Set up subscription for pending result
          const pendingResultHandler = createPendingResultHandler(
            url,
            options,
            resolveWithTiming,
            rejectWithTiming,
            subscriptionRef,
            startTime
          );

          subscriptionRef.current = appEventsMessaging(
            `pending_result_ready_${result.resultId}`,
            { onSubscribe: pendingResultHandler, once: true }
          );

          subscriptionRef.current.subscribe();
        } else {
          // Result is ready, resolve immediately
          resolveWithTiming(result);
        }
      } catch (error) {
        rejectWithTiming(error);
      }
    } catch (error) {
      rejectWithTiming(error);
    }
  });
};
